import {
  GoogleAuthProvider,
  browserPopupRedirectResolver,
  getRedirectResult,
  onAuthStateChanged,
  signInAnonymously,
  signInWithPopup,
  signOut,
  updateProfile,
  type User,
} from "firebase/auth";
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { getDb, getFirebaseAuth, isFirebaseConfigured } from "./config";
import {
  isCapacitorAndroid,
  startAndroidGoogleAuthBridge,
} from "./androidAuthBridge";
import { isAllowlistedAdminUid } from "./adminAllowlist";

export type AccountSnapshot = {
  configured: boolean;
  user: User | null;
  displayName: string;
  email: string | null;
  photoURL: string | null;
  isAnonymous: boolean;
  plan: "free" | "full_course";
  hdSamplePack: boolean;
  /** Server-set Firestore role === "admin" (UX only; APIs still check ADMIN_UIDS). */
  isAdmin: boolean;
};

type Listener = (snap: AccountSnapshot) => void;

const listeners = new Set<Listener>();
let unsubUserDoc: (() => void) | null = null;
/** False until first onAuthStateChanged — avoids false “Signed out” on refresh. */
let authHydrated = false;
let current: AccountSnapshot = {
  configured: isFirebaseConfigured,
  user: null,
  displayName: "",
  email: null,
  photoURL: null,
  isAnonymous: false,
  plan: "free",
  hdSamplePack: false,
  isAdmin: false,
};

function emit(): void {
  for (const l of listeners) l(current);
}

export function isAuthHydrated(): boolean {
  return authHydrated;
}

function nameFromEmail(email: string | null | undefined): string {
  if (!email) return "";
  const local = email.split("@")[0]?.trim() ?? "";
  if (!local) return "";
  return local
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .slice(0, 24);
}

function isPlaceholderName(name: string | null | undefined): boolean {
  const n = (name ?? "").trim().toLowerCase();
  return (
    !n ||
    n === "player" ||
    n === "player one" ||
    n === "piano student" ||
    n === "guest player" ||
    n === "enter player name"
  );
}

function toSnap(
  user: User | null,
  plan: AccountSnapshot["plan"] = "free",
  hdSamplePack = false,
  overrides?: {
    displayName?: string | null;
    photoURL?: string | null;
    customPhoto?: string | null;
    profileCustomized?: boolean;
    isAdmin?: boolean;
  },
): AccountSnapshot {
  const fromAuth = user?.displayName?.trim() || "";
  const fromDoc = overrides?.displayName?.trim() || "";
  const fromEmail = nameFromEmail(user?.email);
  // Google Auth name wins unless the user explicitly customized their profile in-app.
  const displayName = overrides?.profileCustomized && fromDoc && !isPlaceholderName(fromDoc)
    ? fromDoc
    : fromAuth ||
      (!isPlaceholderName(fromDoc) ? fromDoc : "") ||
      (user?.isAnonymous ? "Guest Player" : fromEmail) ||
      fromDoc ||
      "";

  // Custom uploaded photo wins; else Google Auth photo; else Firestore photoURL.
  const photoURL =
    (overrides?.customPhoto?.trim() || null) ||
    user?.photoURL ||
    (overrides?.photoURL?.trim() || null) ||
    null;

  return {
    configured: isFirebaseConfigured,
    user,
    displayName,
    email: user?.email ?? null,
    photoURL,
    isAnonymous: Boolean(user?.isAnonymous),
    plan,
    hdSamplePack,
    isAdmin:
      Boolean(user) &&
      !user?.isAnonymous &&
      (Boolean(overrides?.isAdmin) || isAllowlistedAdminUid(user?.uid)),
  };
}

function planFromData(data: {
  plan?: string;
  entitlements?: { fullCourse?: boolean; hdSamplePack?: boolean };
}): { plan: "free" | "full_course"; hdSamplePack: boolean } {
  const full = Boolean(data.entitlements?.fullCourse || data.plan === "full_course");
  return {
    plan: full ? "full_course" : "free",
    hdSamplePack: Boolean(data.entitlements?.hdSamplePack),
  };
}

type UserDocProfile = {
  displayName?: string | null;
  photoURL?: string | null;
  customPhoto?: string | null;
  profileCustomized?: boolean;
  role?: string | null;
};

function profileOverridesFromDoc(data: UserDocProfile): {
  displayName?: string | null;
  photoURL?: string | null;
  customPhoto?: string | null;
  profileCustomized?: boolean;
  isAdmin?: boolean;
} {
  return {
    displayName: data.displayName ?? null,
    photoURL: data.photoURL ?? null,
    customPhoto: data.customPhoto ?? null,
    profileCustomized: Boolean(data.profileCustomized),
    isAdmin: data.role === "admin",
  };
}

async function ensureUserDoc(user: User): Promise<void> {
  const db = getDb();
  if (!db) return;
  const ref = doc(db, "users", user.uid);
  const snap = await getDoc(ref);
  const authName = user.displayName || nameFromEmail(user.email) || "Player";
  if (!snap.exists()) {
    await setDoc(ref, {
      displayName: authName,
      email: user.email || null,
      photoURL: user.photoURL || null,
      plan: "free",
      entitlements: {
        fullCourse: false,
        hdSamplePack: false,
      },
      profileCustomized: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      app: "ultimate-piano-tutor",
    });
    return;
  }
  // Keep Google name/photo fresh when the user has not customized their profile.
  const data = snap.data() as UserDocProfile;
  const patch: Record<string, unknown> = {};
  if (!data.profileCustomized) {
    if (user.displayName && data.displayName !== user.displayName) {
      patch.displayName = user.displayName;
    } else if (isPlaceholderName(data.displayName) && authName) {
      patch.displayName = authName;
    }
    if (user.photoURL && !data.customPhoto && data.photoURL !== user.photoURL) {
      patch.photoURL = user.photoURL;
    }
  }
  if (Object.keys(patch).length) {
    patch.updatedAt = serverTimestamp();
    await setDoc(ref, patch, { merge: true });
  }
}

function watchUserDoc(user: User): void {
  unsubUserDoc?.();
  unsubUserDoc = null;
  const db = getDb();
  if (!db) {
    current = toSnap(user, "free", false);
    emit();
    return;
  }
  unsubUserDoc = onSnapshot(doc(db, "users", user.uid), (snap) => {
    if (!snap.exists()) {
      current = toSnap(user, "free", false);
      emit();
      return;
    }
    // Guest accounts may practice and sync progress, but premium ownership is
    // recognized only for a verified, non-anonymous Firebase account.
    if (user.isAnonymous) {
      current = toSnap(user, "free", false);
      emit();
      return;
    }
    const data = snap.data() as UserDocProfile & Parameters<typeof planFromData>[0];
    const { plan, hdSamplePack } = planFromData(data);
    current = toSnap(user, plan, hdSamplePack, profileOverridesFromDoc(data));
    emit();
  });
}

export function subscribeAccount(listener: Listener): () => void {
  listeners.add(listener);
  listener(current);
  return () => listeners.delete(listener);
}

export function getAccountSnapshot(): AccountSnapshot {
  return current;
}

/** After adminBootstrap succeeds — unlock UI immediately (don't wait on Firestore). */
export function markCurrentUserAdminLocally(): void {
  if (!current.user || current.user.isAnonymous) return;
  current = {
    ...current,
    isAdmin: true,
    plan: "full_course",
    hdSamplePack: true,
  };
  emit();
}

export async function initAuthAccount(): Promise<void> {
  const auth = getFirebaseAuth();
  if (!auth) {
    authHydrated = true;
    current = toSnap(null, "free", false);
    emit();
    return;
  }

  // Subscribe first so the restored IndexedDB/local session is never missed.
  onAuthStateChanged(auth, async (user) => {
    authHydrated = true;
    unsubUserDoc?.();
    unsubUserDoc = null;
    if (!user) {
      current = toSnap(null, "free", false);
      emit();
      return;
    }
    // Paint Auth profile immediately (don't wait for Firestore).
    current = toSnap(user, current.plan, current.hdSamplePack);
    emit();
    try {
      await ensureUserDoc(user);
    } catch {
      /* rules / offline */
    }
    watchUserDoc(user);
  });

  // Finish Google redirect flow (Electron desktop + Android when popup fails).
  // Never treat a missing redirect as sign-out.
  try {
    const redirected = await getRedirectResult(auth);
    if (redirected?.user) {
      current = toSnap(redirected.user, current.plan, current.hdSamplePack);
      emit();
      try {
        await ensureUserDoc(redirected.user);
      } catch {
        /* */
      }
    }
  } catch (e) {
    console.warn("[auth] getRedirectResult", e);
  }

  try {
    await auth.authStateReady();
  } catch {
    /* */
  }
  authHydrated = true;
  emit();
}

function authErrorMessage(e: unknown): string {
  const code = e && typeof e === "object" && "code" in e ? String((e as { code: string }).code) : "";
  const msg = e instanceof Error ? e.message : "";
  if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
    return "Sign-in window closed. On desktop use the latest app build (localhost auth). In browser open http://localhost:5173 (not 127.0.0.1).";
  }
  if (code === "auth/unauthorized-domain") {
    return "Domain not allowed. Firebase Auth → Settings → Authorized domains must include localhost. Google Cloud OAuth → add http://localhost:18765 and http://localhost:5173.";
  }
  if (code === "auth/popup-blocked") {
    return "Popup blocked. Allow popups, then try Sign in again.";
  }
  if (/requested action is invalid/i.test(msg)) {
    return "Google OAuth setup incomplete — see docs/GOOGLE_SIGNIN_FIX.md.";
  }
  if (msg) return msg;
  return "Sign-in failed.";
}

export async function signInWithGoogle(): Promise<void> {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error("Firebase not configured yet.");

  // Android WebView: Google returns Error 400 (malformed) for embedded OAuth.
  // Open live site in Custom Tabs; app polls Firestore handoff until signed in.
  if (isCapacitorAndroid()) {
    try {
      await startAndroidGoogleAuthBridge();
      const user = auth.currentUser;
      if (!user || user.isAnonymous) {
        throw new Error("Google sign-in did not complete. Try again.");
      }
      try {
        await user.reload();
      } catch {
        /* ignore */
      }
      current = toSnap(user, current.plan, current.hdSamplePack);
      emit();
      try {
        await ensureUserDoc(user);
      } catch {
        /* */
      }
      return;
    } catch (e) {
      throw new Error(authErrorMessage(e));
    }
  }

  const provider = new GoogleAuthProvider();
  provider.addScope("profile");
  provider.addScope("email");
  provider.setCustomParameters({ prompt: "select_account" });

  try {
    const cred = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
    try {
      await cred.user.reload();
    } catch {
      /* ignore */
    }
    current = toSnap(cred.user, current.plan, current.hdSamplePack);
    emit();
  } catch (e) {
    throw new Error(authErrorMessage(e));
  }
}

/** Complete Android deep-link handoff after website Google Sign-In. */
export async function finishAndroidGoogleAuthFromUrl(url: string): Promise<boolean> {
  const auth = getFirebaseAuth();
  // Polling may have already finished sign-in before the deep link arrives.
  if (auth?.currentUser && !auth.currentUser.isAnonymous) {
    return true;
  }
  const { completeAuthBridgeFromDeepLink, closeAndroidAuthBrowser } = await import("./androidAuthBridge");
  let ok = false;
  try {
    ok = await completeAuthBridgeFromDeepLink(url);
  } catch (e) {
    if (auth?.currentUser && !auth.currentUser.isAnonymous) return true;
    throw new Error(authErrorMessage(e));
  }
  if (!ok) return false;
  await closeAndroidAuthBrowser();
  const user = auth?.currentUser;
  if (user) {
    current = toSnap(user, current.plan, current.hdSamplePack);
    emit();
    try {
      await ensureUserDoc(user);
    } catch {
      /* */
    }
  }
  return true;
}


export async function signInAsGuest(): Promise<void> {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error("Firebase not configured yet.");
  await signInAnonymously(auth);
}

export async function signOutAccount(): Promise<void> {
  const auth = getFirebaseAuth();
  if (!auth) return;
  await signOut(auth);
}

export async function saveAccountDisplayName(name: string): Promise<void> {
  const auth = getFirebaseAuth();
  const user = auth?.currentUser;
  if (!user || user.isAnonymous) return;
  const trimmed = name.trim().slice(0, 24);
  if (!trimmed) return;
  await updateProfile(user, { displayName: trimmed });
  const db = getDb();
  if (db) {
    await setDoc(
      doc(db, "users", user.uid),
      {
        displayName: trimmed,
        profileCustomized: true,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  }
  current = { ...current, displayName: trimmed };
  emit();
}

/** Persist a small custom avatar (data URL or https). Auth photoURL only for https. */
export async function saveAccountPhoto(photo: string | null): Promise<void> {
  const auth = getFirebaseAuth();
  const user = auth?.currentUser;
  if (!user || user.isAnonymous) return;
  const db = getDb();
  if (!db) {
    current = { ...current, photoURL: photo };
    emit();
    return;
  }
  const isHttps = Boolean(photo && /^https:\/\//i.test(photo));
  const isData = Boolean(photo && photo.startsWith("data:image/"));
  // Cap custom data URLs so Firestore docs stay small (~400KB soft limit here).
  if (isData && photo && photo.length > 400_000) {
    throw new Error("Photo too large — try a smaller image.");
  }
  await setDoc(
    doc(db, "users", user.uid),
    {
      profileCustomized: true,
      ...(isHttps ? { photoURL: photo, customPhoto: null } : {}),
      ...(isData ? { customPhoto: photo } : {}),
      ...(!photo ? { customPhoto: null } : {}),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
  if (isHttps && photo) {
    try {
      await updateProfile(user, { photoURL: photo });
    } catch {
      /* Auth photoURL optional */
    }
  }
  current = { ...current, photoURL: photo };
  emit();
}
